import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Segmented, Space, message } from 'antd';
import { PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import AddBookingModal from '../components/AddBookingModal';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const BookingList = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [billingFilter, setBillingFilter] = useState('all');
  const { user } = useAuth();

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await api.get('/bookings');
      setBookings(response.data);
    } catch (error) {
      message.error('Failed to load bookings');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (bookingId, status) => {
    try {
      await api.patch(`/bookings/${bookingId}/status`, { status });
      message.success('Status updated');
      fetchBookings();
    } catch (error) {
      message.error(error.response?.data?.message || 'Update failed');
    }
  };

  const handleMarkBilled = async (bookingId) => {
    try {
      await api.patch(`/bookings/${bookingId}/bill`);
      message.success('Marked as billed');
      fetchBookings();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed');
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/bookings/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'bookings-export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success('Export downloaded');
    } catch (error) {
      message.error(error.response?.data?.message || 'Export failed');
    }
  };

  const filteredBookings = bookings.filter((b) => {
    if (billingFilter === 'billed') return b.billed;
    if (billingFilter === 'pending') return !b.billed;
    return true;
  });
  const pendingBillCount = bookings.filter((b) => !b.billed).length;
  const billedCount = bookings.filter((b) => b.billed).length;

  const statusColor = (s) => (s === 'checked_out' ? 'green' : s === 'checked_in' ? 'blue' : s === 'cancelled' ? 'red' : 'default');

  const columns = [
    { title: 'Guest', dataIndex: 'guestName', key: 'guestName', render: (t) => <strong>{t}</strong> },
    { title: 'Hostel', dataIndex: ['hostel', 'name'], key: 'hostel', render: (t) => t || 'N/A' },
    {
      title: 'Room',
      key: 'room',
      render: (_, r) =>
        r.roomNumber ? (
          <>Room {r.roomNumber}{r.bedNumbers?.length ? ` (${r.bedNumbers.join(', ')})` : ''}</>
        ) : (
          '–'
        ),
    },
    { title: 'Check In', dataIndex: 'checkIn', key: 'checkIn', render: (d) => new Date(d).toLocaleDateString() },
    { title: 'Check Out', dataIndex: 'checkOut', key: 'checkOut', render: (d) => new Date(d).toLocaleDateString() },
    {
      title: 'Amount',
      dataIndex: 'totalAmount',
      key: 'amount',
      render: (v) => (v != null ? `$${Number(v).toFixed(2)}` : '–'),
    },
    {
      title: 'Billing',
      key: 'billing',
      render: (_, r) =>
        r.billed ? (
          <Tag color="green">Billed</Tag>
        ) : (r.status === 'confirmed' || r.status === 'checked_in') && (user?.role === 'owner' || user?.role === 'employee') ? (
          <Button size="small" onClick={() => handleMarkBilled(r._id)}>Bill</Button>
        ) : (
          <Tag>Pending</Tag>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s) => <Tag color={statusColor(s)}>{s}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, r) => (
        <Space>
          {r.status === 'confirmed' && (
            <Button type="primary" size="small" onClick={() => handleStatusChange(r._id, 'checked_in')}>Check In</Button>
          )}
          {r.status === 'checked_in' && (
            <Button type="default" size="small" onClick={() => handleStatusChange(r._id, 'checked_out')}>Check Out</Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        title={<span style={{ fontSize: 20, fontWeight: 600 }}>Bookings</span>}
        extra={
          <Space>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>Export CSV</Button>
            {(user?.role === 'owner' || user?.role === 'employee') && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowModal(true)}>Add Booking</Button>
            )}
          </Space>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <Segmented
            options={[
              { label: 'All', value: 'all' },
              { label: `Pending bill (${pendingBillCount})`, value: 'pending' },
              { label: `Billed (${billedCount})`, value: 'billed' },
            ]}
            value={billingFilter}
            onChange={setBillingFilter}
          />
        </div>
<Table
            rowKey="_id"
            columns={columns}
            dataSource={filteredBookings}
            loading={loading}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            scroll={{ x: 'max-content' }}
          />
      </Card>
      {showModal && <AddBookingModal onClose={() => setShowModal(false)} onSuccess={() => { fetchBookings(); setShowModal(false); }} />}
    </>
  );
};

export default BookingList;
